import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';
import type {
  PagamentoInscritosLiga,
  PagamentoInscritosLigaConnection,
  PagamentoInscritosLigaInput,
} from '../types/pagamentoInscritosLiga';
import { StatusPagamento } from '../types/pagamento';

// Re-export types for convenience
export type {
  PagamentoInscritosLiga,
  PagamentoInscritosLigaConnection,
  PagamentoInscritosLigaInput,
} from '../types/pagamentoInscritosLiga';

export type { StatusPagamento } from '../types/pagamento';

// Fragment reutilizável
export const PAGAMENTO_INSCRITOS_LIGA_FRAGMENT = gql`
  fragment PagamentoInscritosLigaFragment on PagamentoInscritosLiga {
    id
    valor
    quantidadeInscritos
    status
    invoiceId
    codigoTransacao
    reciboUrl
    dataVencimento
    dataPagamento
    motivoCancelamento
    dataCancelamento
    criadoEm
    atualizadoEm
    categoriaTorneio {
      id
      nome
      torneio { id nome }
    }
    liga {
      id
      nome
    }
  }
`;

// Queries
export const PAGAMENTO_INSCRITOS_LIGA_POR_ID = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  query PagamentoInscritosLigaPorId($id: ID!) {
    pagamentoInscritosLigaPorId(id: $id) {
      ...PagamentoInscritosLigaFragment
    }
  }
`;

export const PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  query PagamentosInscritosLigaFiltrados(
    $ligaId: ID
    $categoriaTorneioId: ID
    $status: StatusPagamento
    $page: Int = 0
    $size: Int = 10
  ) {
    pagamentosInscritosLigaFiltrados(
      ligaId: $ligaId
      categoriaTorneioId: $categoriaTorneioId
      status: $status
      page: $page
      size: $size
    ) {
      page
      size
      totalItems
      totalPages
      items {
        ...PagamentoInscritosLigaFragment
      }
    }
  }
`;

// Mutations
export const CRIAR_BOLETO_INSCRITOS = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  mutation CriarBoletoInscritos($categoriaTorneioId: ID!) {
    criarBoletoInscritos(categoriaTorneioId: $categoriaTorneioId) {
      ...PagamentoInscritosLigaFragment
    }
  }
`;

export const ATUALIZAR_STATUS_PAGAMENTO_INSCRITOS = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  mutation AtualizarStatusPagamentoInscritos(
    $id: ID!
    $status: String!
    $codigoTransacao: String
    $reciboUrl: String
  ) {
    atualizarStatusPagamentoInscritos(
      id: $id
      status: $status
      codigoTransacao: $codigoTransacao
      reciboUrl: $reciboUrl
    ) {
      ...PagamentoInscritosLigaFragment
    }
  }
`;

export const ATUALIZAR_PAGAMENTO_INSCRITOS_LIGA = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  mutation AtualizarPagamentoInscritosLiga($id: ID!, $input: PagamentoInscritosLigaInput!) {
    atualizarPagamentoInscritosLiga(id: $id, input: $input) {
      ...PagamentoInscritosLigaFragment
    }
  }
`;

export const CANCELAR_BOLETO_INSCRITOS = gql`
  ${PAGAMENTO_INSCRITOS_LIGA_FRAGMENT}
  mutation CancelarBoletoInscritos($id: ID!, $motivo: String) {
    cancelarBoletoInscritos(id: $id, motivo: $motivo) {
      ...PagamentoInscritosLigaFragment
    }
  }
`;

// Interfaces para variáveis das queries/mutations
export interface PagamentoInscritosLigaPorIdData {
  pagamentoInscritosLigaPorId: PagamentoInscritosLiga;
}

export interface PagamentoInscritosLigaPorIdVars {
  id: string;
}

export interface PagamentosInscritosLigaFiltradosData {
  pagamentosInscritosLigaFiltrados: PagamentoInscritosLigaConnection;
}

export interface PagamentosInscritosLigaFiltradosVars {
  ligaId?: string;
  categoriaTorneioId?: string;
  status?: StatusPagamento;
  page?: number;
  size?: number;
}

export interface CriarBoletoInscritosData {
  criarBoletoInscritos: PagamentoInscritosLiga;
}

export interface CriarBoletoInscritosVars {
  categoriaTorneioId: string;
}

export interface AtualizarStatusPagamentoInscritosData {
  atualizarStatusPagamentoInscritos: PagamentoInscritosLiga;
}

export interface AtualizarStatusPagamentoInscritosVars {
  id: string;
  status: string;
  codigoTransacao?: string;
  reciboUrl?: string;
}

export interface AtualizarPagamentoInscritosLigaData {
  atualizarPagamentoInscritosLiga: PagamentoInscritosLiga;
}

export interface AtualizarPagamentoInscritosLigaVars {
  id: string;
  input: PagamentoInscritosLigaInput;
}

export interface CancelarBoletoInscritosData {
  cancelarBoletoInscritos: PagamentoInscritosLiga;
}

export interface CancelarBoletoInscritosVars {
  id: string;
  motivo?: string;
}

// Funções de serviço com tratamento de erro
const handleError = (error: any, defaultMessage: string): never => {
  if (error.graphQLErrors) {
    const graphQLError = error.graphQLErrors[0];
    if (graphQLError?.extensions?.code === 'UNAUTHENTICATED') {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  if (error.response?.status === 401) {
    dispatchLogout();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw error;
  }

  let message = defaultMessage;
  if (error.graphQLErrors?.[0]?.message) {
    message = error.graphQLErrors[0].message;
  } else if (error.message) {
    message = error.message;
  }

  dispatchGlobalError(message);
  throw error;
};

// Service functions - Queries
export const buscarPagamentoInscritosLigaPorId = async (
  id: string,
): Promise<PagamentoInscritosLiga> => {
  try {
    const { data } = await apolloClient.query<
      PagamentoInscritosLigaPorIdData,
      PagamentoInscritosLigaPorIdVars
    >({
      query: PAGAMENTO_INSCRITOS_LIGA_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    return data.pagamentoInscritosLigaPorId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar pagamento de inscritos. Tente novamente.');
  }
};

export const listarPagamentosInscritosLiga = async (params?: {
  ligaId?: string;
  categoriaTorneioId?: string;
  status?: StatusPagamento;
  page?: number;
  size?: number;
}): Promise<PagamentoInscritosLigaConnection> => {
  try {
    const { data } = await apolloClient.query<
      PagamentosInscritosLigaFiltradosData,
      PagamentosInscritosLigaFiltradosVars
    >({
      query: PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS,
      variables: {
        ligaId: params?.ligaId,
        categoriaTorneioId: params?.categoriaTorneioId,
        status: params?.status,
        page: params?.page ?? 0,
        size: params?.size ?? 10,
      },
      fetchPolicy: 'no-cache',
    });
    return data.pagamentosInscritosLigaFiltrados;
  } catch (error: any) {
    return handleError(error, 'Erro ao listar pagamentos de inscritos. Tente novamente.');
  }
};

// Service functions - Mutations
export const criarBoletoInscritos = async (
  categoriaTorneioId: string,
): Promise<PagamentoInscritosLiga> => {
  try {
    const { data } = await apolloClient.mutate<CriarBoletoInscritosData, CriarBoletoInscritosVars>({
      mutation: CRIAR_BOLETO_INSCRITOS,
      variables: { categoriaTorneioId },
    });
    if (!data?.criarBoletoInscritos) {
      throw new Error('Não foi possível gerar o boleto de inscritos.');
    }
    return data.criarBoletoInscritos;
  } catch (error: any) {
    return handleError(error, 'Erro ao gerar boleto de inscritos. Tente novamente.');
  }
};

export const atualizarStatusPagamentoInscritos = async (
  id: string,
  status: string,
  codigoTransacao?: string,
  reciboUrl?: string,
): Promise<PagamentoInscritosLiga> => {
  try {
    const { data } = await apolloClient.mutate<
      AtualizarStatusPagamentoInscritosData,
      AtualizarStatusPagamentoInscritosVars
    >({
      mutation: ATUALIZAR_STATUS_PAGAMENTO_INSCRITOS,
      variables: { id, status, codigoTransacao, reciboUrl },
    });
    if (!data?.atualizarStatusPagamentoInscritos) {
      throw new Error('Não foi possível atualizar o status do pagamento.');
    }
    return data.atualizarStatusPagamentoInscritos;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar status do pagamento. Tente novamente.');
  }
};

export const atualizarPagamentoInscritosLiga = async (
  id: string,
  input: PagamentoInscritosLigaInput,
): Promise<PagamentoInscritosLiga> => {
  try {
    const { data } = await apolloClient.mutate<
      AtualizarPagamentoInscritosLigaData,
      AtualizarPagamentoInscritosLigaVars
    >({
      mutation: ATUALIZAR_PAGAMENTO_INSCRITOS_LIGA,
      variables: { id, input },
    });
    if (!data?.atualizarPagamentoInscritosLiga) {
      throw new Error('Não foi possível atualizar o pagamento.');
    }
    return data.atualizarPagamentoInscritosLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar pagamento de inscritos. Tente novamente.');
  }
};

export const cancelarBoletoInscritos = async (
  id: string,
  motivo?: string,
): Promise<PagamentoInscritosLiga> => {
  try {
    const { data } = await apolloClient.mutate<
      CancelarBoletoInscritosData,
      CancelarBoletoInscritosVars
    >({
      mutation: CANCELAR_BOLETO_INSCRITOS,
      variables: { id, motivo },
    });
    if (!data?.cancelarBoletoInscritos) {
      throw new Error('Não foi possível cancelar o boleto.');
    }
    return data.cancelarBoletoInscritos;
  } catch (error: any) {
    return handleError(error, 'Erro ao cancelar boleto de inscritos. Tente novamente.');
  }
};
